import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FileFilterModel,
  useCurrentWorkspace,
  useFileFilter,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  Colors,
  ColumnData,
  ListCountProvider,
  SelectionStatus,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, Text, View } from 'react-native';
import { useLocation } from 'react-router-dom';
import { ModalButton } from '../../shared/components/modalButton';
import { FileAssetsForm } from './FileAssetsForm';
import { FileAssignmentForm } from './fileAssignmentForm';
import { FilesFilters } from './filesFilters';
import { FilesList, SelectedFileRow } from './filesList';
import { usePreviewMedia } from '../../shared/hooks/useFilePreview';
import {
  useFilesEditColumns,
  useFilesFilterRightColumn,
  useFilesPreviewLeftColumn,
  useFilesPreviewRightColumn,
} from './filesRightColumns';
import { FilesSelectionPanelMain } from './filesSelectionPanel';

export const Files = () => {
  return <FilesContent />;
};

const FilesContent = () => {
  const location = useLocation();
  const path = location.pathname;

  const isFiltersRoute = path === '/files/filters';
  const editMatch = path.match(/^\/files\/([^/]+)\/edit$/);
  const previewMatch = path.match(/^\/files\/([^/]+)$/);
  const editFileId = editMatch ? editMatch[1] : null;
  const previewFileId =
    !editFileId && !isFiltersRoute && previewMatch ? previewMatch[1] : null;

  // Single source of truth for the selected media. Both preview-mode and
  // edit-mode read from this hook instance, so React Query cache results
  // can't diverge between two parallel useFindMedia calls.
  const activeFileId = previewFileId ?? editFileId;
  const previewMedia = usePreviewMedia(activeFileId);

  const filterRightColumn = useFilesFilterRightColumn(isFiltersRoute);
  const previewRightColumn = useFilesPreviewRightColumn(
    previewFileId,
    previewMedia
  );
  const previewLeftColumn = useFilesPreviewLeftColumn(
    editFileId,
    !!editFileId,
    previewMedia
  );
  const { rightColumn: editRightColumn, thirdColumn: editThirdColumn } =
    useFilesEditColumns(editFileId, !!editFileId, previewMedia);

  const urlRightColumn = editFileId
    ? editRightColumn
    : filterRightColumn ?? previewRightColumn;

  // transitionKey drives the preview ↔ edit animation on the unified
  // SlideLayout. It must be undefined in preview mode so the SlideLayout
  // treats edit→preview as "going back" (file list slides in from the left).
  const transitionKey = editFileId ? `edit-${editFileId}` : undefined;

  return (
    <FilesBase
      leftColumn={previewLeftColumn}
      rightColumn={urlRightColumn}
      thirdColumn={editThirdColumn}
      transitionKey={transitionKey}
    />
  );
};

export const FilesBase = ({
  rightColumn: rightColumnOverride,
  leftColumn: leftColumnOverride,
  thirdColumn: thirdColumnOverride,
  viewMode: viewModeOverride,
  transitionKey,
}: {
  rightColumn?: ColumnData;
  leftColumn?: ColumnData;
  thirdColumn?: ColumnData;
  viewMode?: ViewMode;
  transitionKey?: string;
} = {}) => {
  const { t, i18n } = useTranslation();
  const { data: fileFilter } = useFileFilter();
  const currentWorkspace = useCurrentWorkspace();
  const impersonatingWorkspace = useCurrentWorkspace(true);

  const showCheckboxColumn = useMemo(
    () => !!impersonatingWorkspace,
    [impersonatingWorkspace]
  );

  const filter = useMemo(() => {
    const filter: FileFilterModel = fileFilter ?? new FileFilterModel();
    filter.workspaceId = currentWorkspace?.id;
    return filter.getGraphQLFilter();
  }, [fileFilter]);

  const [rowsSelected, setRowsSelected] = useState<SelectedFileRow[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    // When invoked from the filter route, also close the filter panel so the
    // back arrow doesn't just swap back to it after deselecting.
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const CountButton = (data: any) => {
    const isProducerWorkspace =
      currentWorkspace?.type === WorkspaceType.Producer;

    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name={data.iconName}
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.count)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          if (isProducerWorkspace) {
            return (
              <FileAssetsForm
                fileId={data.fileId}
                title={data.name}
                showCheckboxColumn={showCheckboxColumn}
                onComplete={onComplete}
              />
            );
          }

          return (
            <FileAssignmentForm
              fileId={data.fileId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              // if user clicked addons count open addons tab first, else products
              initialTab={data.countType === 'addon' ? 'addons' : 'products'}
              onComplete={onComplete}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const filterComponent = <FilesFilters />;
  const [count, setCount] = useState<number>();

  const columnComponent = (
    <View style={styles.container}>
      <FilesList
        filter={filter}
        showCheckboxColumn={showCheckboxColumn}
        countButton={CountButton}
        isProducerWorkspace={currentWorkspace?.type === WorkspaceType.Producer}
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
        onItemsCountChange={setCount}
      />
    </View>
  );

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection) return undefined;

    const count = rowsSelected.length;
    const title =
      count === 1
        ? t('file.topBar.fileSelected', {
            defaultValue: '1 file selected',
          })
        : t('file.topBar.filesSelected', {
            count,
            defaultValue: `${count} files selected`,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: <FilesSelectionPanelMain fileIds={rowsSelectedIds} />,
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [hasSelection, rowsSelected, rowsSelectedIds, clearSelection, t, i18n]);

  const resolvedRightColumn = selectionRightColumn ?? rightColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride || leftColumnOverride
      ? ViewMode.DUAL
      : ViewMode.SINGLE);

  const defaultLeftColumn: ColumnData = {
    title: 'file.list.files',
    count,
    mainComponent: columnComponent,
    headerComponent: filterComponent,
  };

  return (
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={leftColumnOverride ?? defaultLeftColumn}
        rightColumn={resolvedRightColumn}
        thirdColumn={thirdColumnOverride}
        viewMode={resolvedViewMode}
        transitionKey={transitionKey}
      />
    </ListCountProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
});
